import { INCLUDED_PRICE_DISPLAY } from '@/lib/sales/line-economics';
import { formatQuantityWithUnit } from '@/lib/sales/quantity-display';
import { formatHours, tmBilledDisplay } from '@/lib/sales/tmLine';
import { rowShowsMonetaryAmount, type CustomerFacingLine } from '@/lib/sales/service-components';

/**
 * WHAT ONE ROW OF A CUSTOMER'S INVOICE SAYS — in one place, for every surface
 * that draws one.
 *
 * The invoice PDF, the public invoice table and the client portal are three
 * renderings of ONE document, and they had three copies of this arithmetic.
 * Copies drift, and each drift is a customer opening the same invoice in two
 * places and reading two different things:
 *
 *   - the portal printed a time & materials line as `1` at `$250.00 each`
 *     where the PDF and the public page printed `2 h` at `$125.00/h` — same
 *     invoice, same line, two stories about how the work was billed;
 *   - the portal had no rollup and no "Included" wording at all, so a service
 *     sold at one approved price itemised there and an included part printed a
 *     price;
 *   - the amount had to be summed over `row.lines` rather than read off the
 *     parent, or a rolled-up row under-reported itself.
 *
 * Every one of those was a renderer re-deriving what another renderer had
 * already worked out. So the derivation lives here and the surfaces keep only
 * their markup, which is the part that genuinely differs between a PDF page, an
 * HTML table and a portal card.
 *
 * MONEY FORMATTING IS THE CALLER'S. `@react-pdf` and the web surfaces format
 * currency differently and always have; forcing one on all three would change
 * what today's documents print, which is not what this is for. The DECISIONS —
 * hours-versus-quantity, rate-versus-price, amount-versus-"Included" — are what
 * had to stop being duplicated.
 */
export interface CustomerInvoiceRowFigures {
  /** The Qty cell: billed hours on a T&M line, else quantity with its unit. */
  quantityText: string;
  /** The Price cell: an hourly rate, a unit price, or "Included". */
  unitPriceText: string;
  /** The Total cell: the row's amount, or "Included". */
  lineTotalText: string;
  /**
   * Whether this row states a monetary amount at all. `rowShowsMonetaryAmount`
   * owns the rule; exposed so a surface can also drop its own extras (a
   * "$X each" caption, say) rather than printing a price beside "Included".
   */
  showsAmount: boolean;
  /**
   * The row's amount, summed over every raw line it stands for. A ROLLED_UP row
   * covers the service AND the parts folded into it, so reading the parent's
   * own `lineTotal` would under-report it.
   */
  lineTotal: number;
  /** The unit price that describes the amount actually printed. */
  unitPrice: number;
}

type RowLine = {
  quantity?: number | null;
  unit?: string | null;
  unitPrice?: number | null;
  lineSubtotal?: number | null;
  lineTax?: number | null;
  lineTotal?: number | null;
  priceStrategy?: string | null;
  laborRate?: number | null;
  estimatedHours?: number | null;
  actualHours?: number | null;
  dynamicPricingConfig?: { enabled?: boolean } | null;
};

export function customerInvoiceRowFigures<T extends RowLine>(
  row: CustomerFacingLine<T>,
  formatMoney: (amount: number) => string,
): CustomerInvoiceRowFigures {
  const item = row.line;
  // A time & materials line is billed hours x rate, so its `quantity` counts
  // materials (usually 1) and its `unitPrice` is the collapsed line subtotal.
  // Printing those raw tells the customer they were charged $250 for one of
  // something, which is not how the business billed them.
  const tm = tmBilledDisplay(item);
  // The amount a row PRINTS is before discounts and before tax: the totals
  // block under the lines states Subtotal, the discounts, then each tax, so a
  // row that folded those in did not add up to the Subtotal below it and showed
  // the customer the tax twice. A legacy line without `lineSubtotal` falls back
  // to its total less its own tax.
  const lineTotal = row.lines.reduce(
    (sum, line) =>
      sum +
      (line.lineSubtotal != null
        ? Number(line.lineSubtotal)
        : Number(line.lineTotal ?? 0) - Number(line.lineTax ?? 0)),
    0,
  );
  const quantity = Number(item.quantity) || 0;
  // The unit price must describe the amount actually printed. On a rolled-up
  // row the total covers the service AND its parts, so the service's own unit
  // price would read as "Qty 1 × $280 = $349".
  const unitPrice =
    row.lines.length > 1 && quantity ? lineTotal / quantity : Number(item.unitPrice ?? 0);
  // NOT-637 — an included part is paid for inside the service that installs it,
  // so it says so instead of stating a price. `$0.00` was read as "free", and
  // then as a reason to query what the service charged for.
  const showsAmount = rowShowsMonetaryAmount(row);

  return {
    quantityText: tm ? `${formatHours(tm.hours)} h` : formatQuantityWithUnit(quantity, item.unit),
    unitPriceText: !showsAmount
      ? INCLUDED_PRICE_DISPLAY
      : tm
        ? `${formatMoney(tm.rate)}/h`
        : formatMoney(unitPrice),
    lineTotalText: showsAmount ? formatMoney(lineTotal) : INCLUDED_PRICE_DISPLAY,
    showsAmount,
    lineTotal,
    unitPrice,
  };
}
